<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script setup lang="ts">
import type { FieldOption } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps<{
  fieldKey: string;
  editable?: boolean;
}>();

const reportStore = useUpdatedReportStore();
const theme = computed(() => reportStore.getTheme());

const active = ref(false);
const addingNew = ref(false);
const newOptionText = ref('');
const newInputRef = ref<HTMLInputElement | null>(null);
const dropdownContainer = ref<HTMLElement | null>(null);

const fieldState = computed(() => reportStore.getFieldState(props.fieldKey));

const canAddAdditional = computed(() => !!fieldState.value?.canAddAdditional);

const options = computed<FieldOption[]>(() => {
  return fieldState.value?.options ?? [];
});

const modelValue = computed(() => {
  return reportStore.getValue(props.fieldKey) ?? '';
});

const selectedLabel = computed(() => {
  const { value } = modelValue;
  if (!value) return 'Select...';
  const match = options.value.find(o => o.id === value);
  return match?.label ?? String(value);
});

function toggleDropdown() {
  active.value = !active.value;
  if (!active.value) {
    addingNew.value = false;
  }
}

function selectOption(optionId: string) {
  reportStore.setValue(props.fieldKey, optionId);
  active.value = false;
  addingNew.value = false;
}

function startAddingNew(e: MouseEvent) {
  e.stopPropagation();
  addingNew.value = true;
  nextTick(() => {
    newInputRef.value?.focus();
  });
}

function submitNewOption() {
  const trimmed = newOptionText.value.trim();
  if (trimmed) {
    const newOption: FieldOption = { id: trimmed, label: trimmed };
    reportStore.setValue(props.fieldKey, trimmed);
    reportStore.addDropdownOption(props.fieldKey, newOption);
    newOptionText.value = '';
    active.value = false;
    addingNew.value = false;
  }
}

function handleDocumentClick(e: MouseEvent) {
  if (
    dropdownContainer.value
    && !dropdownContainer.value.contains(e.target as Node)
  ) {
    active.value = false;
    addingNew.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<template>
  <!-- EDIT MODE -->
  <div
    v-if="editable"
    ref="dropdownContainer"
    class="custom-dropdown"
    :style="{ borderRadius: `${theme?.borderRadius ?? 4}px` }"
  >
    <div
      class="selected-value-container"
      :style="{
        borderColor: theme?.colors.border ?? '#e2e8f0',
        borderRadius: `${theme?.borderRadius ?? 4}px`,
        color: theme?.colors.text ?? '#000000',
      }"
      @click="toggleDropdown"
    >
      <span class="selected-label">{{ selectedLabel }}</span>
      <i
        class="fa-solid fa-caret-down caret-icon"
        :class="{ flipped: active }"
        :style="{ color: theme?.colors.muted ?? '#64748b' }"
      />
    </div>

    <Transition name="slide-fade">
      <div
        v-if="active"
        class="dropdown-options-list"
        :style="{
          borderColor: theme?.colors.border ?? '#e2e8f0',
          borderRadius: `${theme?.borderRadius ?? 4}px`,
          backgroundColor: '#ffffff',
        }"
      >
        <!-- Options list -->
        <div class="options-scroll-container">
          <div
            v-for="option in options"
            :key="option.id"
            class="dropdown-option-item"
            :class="{ selected: option.id === modelValue }"
            :style="{
              color: theme?.colors.text ?? '#000000',
            }"
            @click="selectOption(option.id)"
          >
            {{ option.label }}
          </div>
        </div>

        <!-- Add New option if allowed -->
        <div v-if="canAddAdditional" class="add-new-option-container">
          <div
            v-if="!addingNew"
            class="add-new-trigger"
            :style="{ color: theme?.colors.muted ?? '#64748b' }"
            @click="startAddingNew"
          >
            <i class="fa-solid fa-plus" />
            <span>Add New</span>
          </div>

          <div v-else class="add-new-input-wrap">
            <input
              ref="newInputRef"
              v-model="newOptionText"
              type="text"
              class="add-new-input"
              placeholder="New option..."
              maxlength="50"
              aria-label="New Option Input"
              @keydown.enter.stop="submitNewOption"
            >
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <!-- READ ONLY -->
  <div v-else>
    <h5
      class="readonly-selected-option"
      :style="{ color: theme?.colors.text ?? '#000000' }"
    >
      {{ selectedLabel }}
    </h5>
  </div>
</template>

<style scoped>
.custom-dropdown {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.selected-value-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #e8eaed;
  background: white;
  padding: 2px 8px;
  height: 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-sizing: border-box;
  user-select: none;
}

.selected-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.caret-icon {
  font-size: 11px;
  transition: transform 150ms ease;
  margin-left: 6px;
}

.caret-icon.flipped {
  transform: rotate(180deg);
}

.dropdown-options-list {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 100;
  border: 1px solid #e8eaed;
  background: white;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  margin-top: 2px;
  max-height: 220px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.options-scroll-container {
  overflow-y: auto;
  flex: 1;
  max-height: 180px;
}

.dropdown-option-item {
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-option-item:hover {
  background-color: #f1f5f9;
}

.dropdown-option-item.selected {
  background-color: #e2e8f0;
  font-weight: 700;
}

.add-new-option-container {
  border-top: 1px solid #cbd5e1;
  background-color: #f8fafc;
  padding: 6px 2px;
  box-sizing: border-box;
}

.add-new-trigger {
  margin-left: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
}

.add-new-trigger:hover {
  color: #000;
}

.add-new-input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100%;
}

.add-new-input {
  flex: 1;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  outline: none;
  color: black;
  min-width: 100%;
}

.add-new-input:focus {
  border-color: var(--md-primary);
}

.add-new-btn {
  font-size: 16px;
  color: var(--md-primary);
  cursor: pointer;
  transition: color 150ms;
}

.add-new-btn:hover {
  color: var(--md-primary);
}

.readonly-selected-option {
  font-size: 13px;
  font-weight: bold;
}

/* Slide Fade Transition */
.slide-fade-enter-active {
  transition: all 150ms ease-out;
}
.slide-fade-leave-active {
  transition: all 150ms cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-2px);
  opacity: 0;
}
</style>
